import {
  Layout,
  Palette,
  Sparkles,
  Zap,
  Book,
  type LucideIcon,
} from "lucide-react";

export type SlideStyle =
  | "minimal"
  | "educational"
  | "corporate"
  | "creative"
  | "data-driven";

export interface TemplateConfig {
  padding: string;
  titleSize: string;
  contentSize: string;
  fontWeight: string;
  spacing: string;
  letterSpacing: string;
  textTransform: string;
  titleBorder: boolean;
  showIcon: boolean;
  bulletIcon: string;
  renderBackground: (bgColor: string, primaryColor: string) => any;
  renderDecorations: (primaryColor: string) => React.ReactNode;
}

export interface SlideStyleOption {
  value: SlideStyle;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
}

export interface ColorSchemeOption {
  value: string;
  label: string;
  color: string;
}

export const SLIDE_STYLE_OPTIONS: SlideStyleOption[] = [
  {
    value: "minimal",
    label: "Minimalista",
    description: "Limpo e focado",
    icon: Layout,
    color: "#188038",
  },
  {
    value: "educational",
    label: "Educacional",
    description: "Amigável e dinâmico",
    icon: Book,
    color: "#1967d2",
  },
  {
    value: "corporate",
    label: "Corporativo",
    description: "Profissional e formal",
    icon: Sparkles,
    color: "#9334e6",
  },
  {
    value: "creative",
    label: "Criativo",
    description: "Inovador e colorido",
    icon: Zap,
    color: "#d93025",
  },
  {
    value: "data-driven",
    label: "Dados",
    description: "Com gráficos e números",
    icon: Palette,
    color: "#ea8600",
  },
];

export const COLOR_SCHEME_OPTIONS: ColorSchemeOption[] = [
  { value: "blue", label: "Azul", color: "#1967d2" },
  { value: "green", label: "Verde", color: "#188038" },
  { value: "purple", label: "Roxo", color: "#9334e6" },
  { value: "red", label: "Vermelho", color: "#d93025" },
  { value: "dark", label: "Escuro", color: "#202124" },
];

export const SlideTemplates: Record<SlideStyle, TemplateConfig> = {
  minimal: {
    padding: "p-12",
    titleSize: "text-5xl",
    contentSize: "text-lg",
    fontWeight: "font-light",
    spacing: "space-y-3",
    letterSpacing: "tracking-wide",
    textTransform: "uppercase",
    titleBorder: false,
    showIcon: false,
    bulletIcon: "—",
    renderBackground: (bgColor, primaryColor) => ({
      backgroundColor: bgColor,
    }),
    renderDecorations: () => null,
  },
  educational: {
    padding: "p-12",
    titleSize: "text-4xl",
    contentSize: "text-base",
    fontWeight: "font-bold",
    spacing: "space-y-4",
    letterSpacing: "tracking-normal",
    textTransform: "capitalize",
    titleBorder: true,
    showIcon: true,
    bulletIcon: "●",
    renderBackground: (bgColor, primaryColor) => ({
      backgroundColor: bgColor,
    }),
    renderDecorations: (primaryColor) => (
      <div
        className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-20"
        style={{ backgroundColor: primaryColor }}
      />
    ),
  },
  corporate: {
    padding: "p-16",
    titleSize: "text-5xl",
    contentSize: "text-sm",
    fontWeight: "font-semibold",
    spacing: "space-y-2",
    letterSpacing: "tracking-tight",
    textTransform: "none",
    titleBorder: false,
    showIcon: true,
    bulletIcon: "►",
    renderBackground: (bgColor, primaryColor) => ({
      backgroundColor: "#ffffff",
      backgroundImage: `linear-gradient(135deg, ${primaryColor}10 0%, transparent 100%)`,
    }),
    renderDecorations: (primaryColor) => (
      <>
        <div
          className="absolute left-0 bottom-0 w-2 h-full"
          style={{ backgroundColor: primaryColor }}
        />
      </>
    ),
  },
  creative: {
    padding: "p-10",
    titleSize: "text-6xl",
    contentSize: "text-lg",
    fontWeight: "font-bold",
    spacing: "space-y-5",
    letterSpacing: "tracking-wider",
    textTransform: "uppercase",
    titleBorder: false,
    showIcon: false,
    bulletIcon: "✓",
    renderBackground: (bgColor, primaryColor) => ({
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${bgColor} 100%)`,
    }),
    renderDecorations: (primaryColor) => (
      <div
        className="absolute -right-20 -bottom-20 w-48 h-48 rounded-full blur-3xl opacity-30"
        style={{ backgroundColor: primaryColor }}
      />
    ),
  },
  "data-driven": {
    padding: "p-12",
    titleSize: "text-4xl",
    contentSize: "text-sm",
    fontWeight: "font-medium",
    spacing: "space-y-3",
    letterSpacing: "tracking-normal",
    textTransform: "none",
    titleBorder: true,
    showIcon: true,
    bulletIcon: "◆",
    renderBackground: (bgColor, primaryColor) => ({
      backgroundColor: "#f5f5f5",
    }),
    renderDecorations: (primaryColor) => (
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: primaryColor }}
      />
    ),
  },
};
